import React, { ReactNode, useState } from 'react';

import { faCircleInfo, faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';

import { useClickOutside } from 'hooks/useClickOutside';

const ModalTrigger = styled.span<{ $triggerMargin?: string }>`
  cursor: pointer;
  font-size: 0.875rem;
  grid-column-gap: 0.5rem;
  align-items: center;
  font-weight: 500;
  display: inline-flex;
  ${(props) => props.$triggerMargin && `margin: ${props.$triggerMargin}`};

  &:hover {
    border-bottom: 1px solid ${(props) => props.theme.colors.ttRed};
  }
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.theme.colors.darkTransparentBlack};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalBody = styled.div`
  font-weight: 500;

  a {
    color: ${(props) => props.theme.colors.black};
  }
`;

const ModalContent = styled.div<{ $closeAnimation: boolean }>`
  background-color: ${(props) => props.theme.colors.white};
  width: 40rem;
  grid-column-gap: 1.5rem;
  grid-row-gap: 1.5rem;
  color: ${(props) => props.theme.colors.primaryBlack};
  flex-direction: column;
  padding: 5rem;
  display: flex;
  position: relative;
  animation: ${(props) => (props.$closeAnimation ? 'slideDown' : 'slideUp')} 0.5s ease-in-out forwards;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideDown {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(100%);
    }
  }
`;

const ModalClose = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
  background-color: ${(props) => props.theme.colors.ttRed};
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  color: ${(props) => props.theme.colors.white};
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
`;

const StyledIcon = styled(FontAwesomeIcon)`
  font-size: 0.75rem;
`;

const ModalHeader = styled.h2`
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};
  text-transform: uppercase;
`;

interface ModalProps {
  header: ReactNode | string;
  body: ReactNode | string;
  trigger: ReactNode | string;
  showInfoIcon?: boolean;
  triggerMargin?: string;
  className?: string;
}

const Modal = ({ header, body, trigger, showInfoIcon, triggerMargin }: ModalProps) => {
  const [closeAnimate, setCloseAnimate] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const ref = useClickOutside(() => closeModal());

  const handleOpenModal = () => {
    setTimeout(() => setShowModal(true), 10);
  };

  const closeModal = () => {
    setCloseAnimate(true);
    setTimeout(() => {
      setShowModal(false);
      setCloseAnimate(false);
    }, 400);
  };

  return (
    <>
      <ModalTrigger $triggerMargin={triggerMargin} onClick={handleOpenModal}>
        {showInfoIcon && <StyledIcon icon={faCircleInfo} />}
        {trigger}
      </ModalTrigger>
      {showModal && (
        <ModalContainer>
          <ModalContent $closeAnimation={closeAnimate} ref={ref}>
            <ModalClose onClick={closeModal}>
              <FontAwesomeIcon icon={faClose} />
            </ModalClose>
            <ModalHeader>{header}</ModalHeader>
            <ModalBody>{body}</ModalBody>
          </ModalContent>
        </ModalContainer>
      )}
    </>
  );
};

export default Modal;
